import { DocumentNode, gql } from '@apollo/client';
import {
  Divider,
  HStack,
  IconButton,
  LinkBox,
  LinkOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { Card } from '@/imports/ui/chakra/components/Card';
import { ChevronDownIcon, ChevronUpIcon } from '@/imports/ui/chakra/feather';

import { FamilyCard_GroupSearchNodeFragment } from './graphql-types/FamilyCard_GroupSearchNodeFragment';

import { JoinButton } from '../JoinButton';
import { ListItem } from '../ListItem';

export const FamilyCard: React.FC<{
  groupSearchNode: FamilyCard_GroupSearchNodeFragment;
  showJoinOptions: boolean;
  defaultIsOpen: boolean;
  isAdminSite: boolean;
}> & {
  groupSearchNodeFragment: DocumentNode;
} = ({ groupSearchNode, showJoinOptions, defaultIsOpen, isAdminSite }) => {
  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen });
  return (
    <Card w="full">
      <VStack divider={<Divider />} spacing="0">
        <LinkBox w="full">
          <HStack
            w="full"
            justifyContent="space-between"
            px="4"
            py={groupSearchNode.hit ? 4 : 2}
          >
            {groupSearchNode.hit ? (
              <ListItem
                showHiddenTag={false}
                group={groupSearchNode.group}
                href={
                  isAdminSite
                    ? `/admin/community/groups/${groupSearchNode.group._id}/settings`
                    : `/community/groups/${groupSearchNode.group._id}/activity`
                }
              />
            ) : (
              <LinkOverlay
                as={NextLink}
                href={
                  isAdminSite
                    ? `/admin/community/groups/${groupSearchNode.group._id}/settings`
                    : `/community/groups/${groupSearchNode.group._id}/activity`
                }
              >
                <Text textStyle="detailStrong">
                  {groupSearchNode.group.name}
                </Text>
              </LinkOverlay>
            )}
            <HStack pr={groupSearchNode.subgroups.length > 0 ? undefined : 10}>
              {!!showJoinOptions && groupSearchNode.hit && (
                <JoinButton
                  group={{
                    ...groupSearchNode.group,
                    settings: {
                      ...groupSearchNode.group.settings,
                      isAutomated:
                        groupSearchNode.group.settings?.isAutomated ??
                        undefined,
                    },
                  }}
                />
              )}
              {groupSearchNode.subgroups.length > 0 &&
                (isOpen ? (
                  <IconButton
                    aria-label="hide subgroups"
                    icon={<ChevronUpIcon />}
                    onClick={onClose}
                    variant="ghost"
                    colorScheme="gray"
                    size="sm"
                  />
                ) : (
                  <IconButton
                    aria-label="show subgroups"
                    icon={<ChevronDownIcon />}
                    onClick={onOpen}
                    variant="ghost"
                    colorScheme="gray"
                    size="sm"
                  />
                ))}
            </HStack>
          </HStack>
        </LinkBox>
        {isOpen &&
          groupSearchNode.subgroups.map((subgroup) => {
            return (
              <LinkBox w="full" key={subgroup.group._id}>
                <HStack w="full" justifyContent="space-between" p="4">
                  <ListItem
                    showHiddenTag={false}
                    group={subgroup.group}
                    href={
                      isAdminSite
                        ? `/admin/community/groups/${subgroup.group._id}/settings`
                        : `/community/groups/${subgroup.group._id}/activity`
                    }
                  />
                  <HStack pr="10">
                    {!!showJoinOptions && (
                      <JoinButton
                        group={{
                          ...subgroup.group,
                          settings: {
                            ...subgroup.group.settings,
                            isAutomated:
                              subgroup.group.settings?.isAutomated ?? undefined,
                          },
                        }}
                      />
                    )}
                  </HStack>
                </HStack>
              </LinkBox>
            );
          })}
      </VStack>
    </Card>
  );
};

FamilyCard.groupSearchNodeFragment = gql`
  fragment FamilyCard_GroupSearchNodeFragment on GroupSearchNode {
    group {
      _id
      settings {
        isAutomated
        findPermission
      }
      ...JoinButton_GroupFragment
      ...GroupListItem_GroupFragment
      currentUserGroupLink {
        _id
      }
    }
    hit
    subgroups {
      group {
        _id
        settings {
          isAutomated
          findPermission
        }
        ...JoinButton_GroupFragment
        ...GroupListItem_GroupFragment
        currentUserGroupLink {
          _id
        }
      }
      hit
    }
  }
  ${JoinButton.groupFragment}
  ${ListItem.groupFragment}
`;
